"use client";

import { createContext, useContext, type FC, type ReactNode } from "react";
import { styled } from "@mui/material/styles";
import { useField } from "formik";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSquare } from "@fortawesome/pro-regular-svg-icons/faSquare";
import { faSquareCheck } from "@fortawesome/pro-solid-svg-icons/faSquareCheck";
import { SANS_FONT_STACK } from "@portal/app/_components/theme/options/typography";

type MultiSelectValue = (string | number | boolean)[];

interface MultiSelectContextShape {
  values: MultiSelectValue;
  toggleValue?: (value: string | number | boolean) => void;
}

const MultiSelectContext = createContext<MultiSelectContextShape>({
  values: [],
  toggleValue: undefined,
});

interface MultiSelectGroupProps {
  values: MultiSelectValue;
  onChange?: (newValues: MultiSelectValue) => void;
  children: ReactNode;
}

export const MultiSelectGroup: FC<MultiSelectGroupProps> = ({ values, onChange, children }) => {
  const toggleValue = (value: string | number | boolean) => {
    if (!onChange) return;

    const currentValues = values || [];
    const valueIndex = currentValues.indexOf(value);

    if (valueIndex === -1) {
      // Add value
      onChange([...currentValues, value]);
    } else {
      // Remove value
      onChange(currentValues.filter((v) => v !== value));
    }
  };

  return (
    <GroupWrapper>
      <MultiSelectContext.Provider value={{ values: values || [], toggleValue }}>
        {children}
      </MultiSelectContext.Provider>
    </GroupWrapper>
  );
};

interface MultiSelectItemProps {
  value: string | number | boolean;
  children: ReactNode;
  disabled?: boolean;
  "data-testid"?: string;
}

export const MultiSelectItem: FC<MultiSelectItemProps> = ({
  value,
  children,
  disabled,
  "data-testid": dataTestid = `multiselect-${value}`,
}) => {
  const { values, toggleValue } = useContext(MultiSelectContext);
  const isChecked = values.includes(value);

  return (
    <CheckboxButton
      type="button"
      className={isChecked ? "active" : ""}
      onClick={() => !disabled && toggleValue?.(value)}
      disabled={disabled}
      data-testid={dataTestid}
      aria-pressed={isChecked}
      aria-label={`${isChecked ? "Deselect" : "Select"} ${children}`}
    >
      <CheckboxIcon className="multiselect-checkbox">
        <FontAwesomeIcon icon={isChecked ? faSquareCheck : faSquare} />
      </CheckboxIcon>
      <LabelText className="multiselect-label">{children}</LabelText>
    </CheckboxButton>
  );
};

interface MultiSelectGroupWithFormikProps {
  name: string;
  children: ReactNode;
  onChange?: (newValues: MultiSelectValue) => void;
}

export const MultiSelectGroupWithFormik: FC<MultiSelectGroupWithFormikProps> = ({
  children,
  name,
  onChange,
}) => {
  const [field, , helpers] = useField<MultiSelectValue>(name);

  const handleChange = async (newValues: MultiSelectValue) => {
    await helpers.setValue(newValues, true);
    await helpers.setTouched(true);
    if (onChange) {
      onChange(newValues);
    }
  };

  return (
    <MultiSelectGroup values={field.value || []} onChange={handleChange}>
      {children}
    </MultiSelectGroup>
  );
};

// Styled components
const GroupWrapper = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  max-width: 400px;
`;

const CheckboxButton = styled("button")`
  display: flex;
  align-items: center;
  padding: 16px 20px;
  width: 100%;
  border: 1px solid ${({ theme }) => theme.palette.dream.grayLight};
  border-radius: 12px;
  background-color: #ffffff;
  cursor: pointer;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease;
  text-align: left;

  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.palette.dream.grayLight};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(34, 34, 85, 0.2);
  }

  &.active {
    border-color: ${({ theme }) => theme.palette.primary.main};
    background-color: ${({ theme }) => theme.palette.secondary.light};

    .multiselect-checkbox {
      color: ${({ theme }) => theme.palette.primary.main};
    }
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CheckboxIcon = styled("div")`
  font-size: 1.25rem;
  color: ${({ theme }) => theme.palette.dream.grayMedium};
  margin-right: 14px;
  flex-shrink: 0;
  transition: color 0.2s ease;
`;

const LabelText = styled("span")`
  font-family: ${SANS_FONT_STACK};
  font-size: 1rem;
  font-weight: 400;
  color: ${({ theme }) => theme.palette.primary.dark};
  line-height: 1.4;
`;
